import {
	applyRiskTemplate,
	createRiskConfig,
	duplicateRiskConfig,
	checkRiskConfigName,
	setRiskConfigNameCheck,
} from '@Actions/Risk.actions';
import { getRiskTemplateCatalog } from '@Actions/RiskTemplateCatalog.actions';
import {
	StylovyzeDialog,
	decoratedAttributes,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { DialogProps, CircularProgress } from '@mui/material';
import {
	selectRiskConfigIsCreating,
	selectRiskConfigNameCheck,
} from '@Selectors/Risk.selectors';
import { selectAssetStore } from '@Selectors';
import {
	selectRiskTemplateCatalogStatus,
	selectRiskTemplateCatalogTemplates,
} from '@Selectors/RiskTemplateCatalog.selectors';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ConfigPublishState } from '@Types/riskConfig.types';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { CreateConfig } from '@Types/risk.types';
import { useGlobalization } from '@Translations/useGlobalization';
import { selectRiskConfigsPage } from '@innovyze/lib-am-common/Selectors';
import { RiskConfigCard } from '@innovyze/lib-am-common';
import { useIsRiskTemplatesEnabled } from '@Hooks/useIsRiskTemplatesEnabled';
import { useRiskDetailsForm } from './useRiskDetailsForm';
import { RiskDetailsStep } from './RiskDetailsStep.component';
import {
	TemplateStartStep,
	StartMethod,
} from '@Components/RiskSetup/templates/TemplateStartStep.component';
import { TemplateInfoDrawer } from '@Components/RiskSetup/templates/TemplateInfoDrawer.component';

interface Props {
	duplicate?: RiskConfigCard;
	open: boolean;
	onClose: () => void;
}

// New scratch models default to Multiply with the redesign (BE-8 persists it at create).
const createMethodFor = (
	isRiskTemplatesEnabled: boolean,
): Pick<CreateConfig, 'riskMethod'> =>
	isRiskTemplatesEnabled ? { riskMethod: 'multiplied' } : {};

export const NewRisk = ({ open, onClose, duplicate }: Props) => {
	const isSaveFilterOn = useIsFeatureEnabled('info-360-assets-save-filter');
	const isRiskTemplatesEnabled = useIsRiskTemplatesEnabled();

	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const riskPage = selectRiskConfigsPage();
	const isCreating = selectRiskConfigIsCreating();
	const nameCheck = selectRiskConfigNameCheck();
	const assetStore = selectAssetStore();

	const form = useRiskDetailsForm({ open, duplicate });

	const [step, setStep] = useState<1 | 2>(1);
	const [startMethod, setStartMethod] = useState<StartMethod>('');
	const [selectedTemplateId, setSelectedTemplateId] = useState('');
	const [helpOpen, setHelpOpen] = useState(false);
	// set on Continue, so only an outcome this dialog asked for can move it on
	const [isAwaitingNameCheck, setIsAwaitingNameCheck] = useState(false);
	const isCheckingName = nameCheck === 'checking';

	const catalogTemplates = useSelector(selectRiskTemplateCatalogTemplates);
	const catalogStatus = useSelector(selectRiskTemplateCatalogStatus);
	const templates = isRiskTemplatesEnabled ? catalogTemplates : [];
	const isCatalogLoading =
		isRiskTemplatesEnabled && !duplicate && catalogStatus === 'loading';

	useEffect(() => {
		setStartMethod('');
		setSelectedTemplateId('');
		if (
			isRiskTemplatesEnabled &&
			!duplicate &&
			form.systemType &&
			form.assetType
		) {
			dispatch(
				getRiskTemplateCatalog({
					systemTypeId: form.systemType,
					assetTypeId: form.assetType,
				}),
			);
		}
	}, [isRiskTemplatesEnabled, duplicate, form.systemType, form.assetType]);

	useEffect(() => {
		setStep(1);
		setStartMethod('');
		setSelectedTemplateId('');
		setHelpOpen(false);
	}, [open, duplicate]);

	const hasStartChoice =
		isRiskTemplatesEnabled && templates.length > 0 && !duplicate;

	// The name can't change on step 2, so an error arriving there came from the
	// server rejecting it on create; go back to step 1 where the error shows.
	useEffect(() => {
		if (step === 2 && form.configNameError.length !== 0) {
			setStep(1);
		}
	}, [step, form.configNameError]);

	const handleCancelNewModel = () => {
		onClose();
	};

	const handleCreateNewModel = (method: StartMethod = startMethod) => {
		if (form.configNameError.length !== 0) return;
		if (duplicate?.configId) {
			dispatch(
				duplicateRiskConfig({
					history: history,
					config: {
						name: form.newName.trim(),
						assetFilterId: form.assetFilterId || undefined,
					},
					configId: duplicate.configId,
					isRiskTemplatesEnabled,
				}),
			);
			return;
		}
		if (isRiskTemplatesEnabled && method === 'template') {
			const selected = templates.find(
				tpl => tpl.id === selectedTemplateId,
			);
			if (!selected) return;
			dispatch(
				applyRiskTemplate({
					history,
					page: riskPage.page,
					templateLabel: selected.label,
					config: {
						name: form.newName.trim(),
						systemType: form.systemType,
						assetType: form.assetType,
						assetFilterId: form.assetFilterId || undefined,
						publishState: ConfigPublishState.unpublished,
						inspectionStandard:
							(form.inspectionStandard as InspectionStandardType) ||
							undefined,
						templateId: selected.id,
					},
				}),
			);
			return;
		}
		dispatch(
			createRiskConfig({
				history: history,
				config: {
					name: form.newName.trim(),
					systemType: form.systemType,
					assetType: form.assetType,
					assetFilterId: form.assetFilterId || undefined,
					publishState: ConfigPublishState.unpublished,
					inspectionStandard:
						(form.inspectionStandard as InspectionStandardType) ||
						undefined,
					...createMethodFor(isRiskTemplatesEnabled),
				},
				page: riskPage.page,
				isRiskTemplatesEnabled,
			}),
		);
	};

	const goNextFromDetails = () => {
		if (!form.isValid) return;
		if (!hasStartChoice) {
			handleCreateNewModel('scratch');
			return;
		}
		setIsAwaitingNameCheck(true);
		dispatch(checkRiskConfigName(form.newName.trim()));
	};

	// The saga has already shown the error for a taken name or a failed check.
	useEffect(() => {
		if (!isAwaitingNameCheck) return;
		if (nameCheck === 'idle' || nameCheck === 'checking') return;
		setIsAwaitingNameCheck(false);
		dispatch(setRiskConfigNameCheck('idle'));
		if (nameCheck === 'free') {
			setStep(2);
		}
	}, [isAwaitingNameCheck, nameCheck]);

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: open,
		maxWidth: 'md',
		onClose: onCloseDialog,
	};

	const adpAttrs = decoratedAttributes({
		name: form.newName,
		systemtype: form.systemType,
		inspectionstandard: form.inspectionStandard,
		assettype: form.assetType,
	});

	const getConfirmText = () => {
		if (duplicate) return t('Duplicate');
		if (step === 1 && hasStartChoice) return t('Continue');
		return t('Create Risk Model');
	};

	const confirmText = getConfirmText();

	const isBusy = isCreating || isCheckingName;

	const confirmDisabled =
		isBusy ||
		(step === 1 ? !form.isValid || isCatalogLoading : startMethod === '');

	return (
		<>
			<StylovyzeDialog
				dialogProps={dialogProps}
				data-cy="new-dialog"
				title={
					duplicate ? t('Duplicate Risk Model') : t('New Risk Model')
				}
				open={open}
				cancelText={step === 2 ? t('Back') : t('Cancel')}
				cancelButtonProps={{
					disabled: isBusy,
					dataCy: 'create-cancel',
				}}
				onCancel={step === 2 ? () => setStep(1) : handleCancelNewModel}
				confirmText={confirmText}
				confirmButtonProps={{
					disabled: confirmDisabled,
					startIcon: isBusy ? (
						<CircularProgress size={16} color="inherit" />
					) : undefined,
					dataCy: 'create-confirm',
					'adp-click': 'true',
					...adpAttrs,
					'adp-component-id': 'create-risk-model-confirm',
					'adp-component-name': 'Create Risk Model',
				}}
				onConfirm={
					step === 1
						? goNextFromDetails
						: () => handleCreateNewModel()
				}>
				{step === 1 ? (
					<RiskDetailsStep
						form={form}
						duplicate={duplicate}
						isSaveFilterOn={!!isSaveFilterOn}
						assetFilters={assetStore.assetFilters ?? []}
						disabled={isCheckingName}
					/>
				) : (
					<TemplateStartStep
						templates={templates}
						startMethod={startMethod}
						selectedTemplateId={selectedTemplateId}
						onSelect={(method, templateId) => {
							setStartMethod(method);
							setSelectedTemplateId(templateId ?? '');
						}}
						onInfo={() => setHelpOpen(true)}
					/>
				)}
			</StylovyzeDialog>
			<TemplateInfoDrawer
				open={helpOpen}
				onClose={() => setHelpOpen(false)}
			/>
		</>
	);
};
